(() => {
  const interactionPauseDuration = 1500;
  const modulo = (value, divisor) => ((value % divisor) + divisor) % divisor;

  const initReel = (reel) => {
    if (reel.dataset.artistReelReady === 'true') return;

    const track = reel.querySelector('[data-artist-reel-track]');
    const slides = [...reel.querySelectorAll('[data-artist-reel-slide]')];
    const root = reel.closest('.artist-reel-test__collaboration');
    const buttons = [...root.querySelectorAll('[data-artist-reel-step]')];
    const current = root.querySelector('[data-artist-reel-current]');
    const currentCaption = root.querySelector('[data-artist-reel-caption]');
    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (!track || !slides.length) return;

    reel.dataset.artistReelReady = 'true';
    slides.forEach((slide, index) => { slide.dataset.artistReelIndex = String(index); });

    let animationFrame = 0;
    let lastTime = performance.now();
    let resumeAt = 0;
    let baseWidth = 0;
    let loopStart = 0;
    let slideStarts = [];
    let allSlides = slides;
    let lastActive = -1;
    let resizeTimer = 0;
    let wheelTimer = 0;
    let pointerId = null;
    let pointerStartX = 0;
    let pointerStartY = 0;
    let pointerStartOffset = 0;
    let pointerAxis = null;
    let touchId = null;
    let touchStartX = 0;
    let touchStartY = 0;
    let touchStartOffset = 0;
    let touchAxis = null;
    let dragDistance = 0;
    let snap = null;

    const carousel = reel.matches('.artist-reel-test__carousel') ? reel : reel.querySelector('.artist-reel-test__carousel');
    if (!carousel) return;

    const keepInLoop = () => {
      if (!baseWidth || slides.length < 2) return;
      while (offset < loopStart - baseWidth) offset += baseWidth;
      while (offset > loopStart + (2 * baseWidth)) offset -= baseWidth;
    };

    let offset = 0;
    const render = () => {
      keepInLoop();
      track.style.transform = `translate3d(${-offset}px,0,0)`;
    };

    const activeIndex = () => {
      if (!baseWidth || slides.length < 2) return 0;
      const local = modulo(offset - loopStart, baseWidth);
      let index = 0;
      slideStarts.forEach((start, candidate) => {
        if (local >= start - 1) index = candidate;
      });
      return index;
    };

    const update = (forcedIndex) => {
      const index = Number.isInteger(forcedIndex) ? forcedIndex : activeIndex();
      if (index === lastActive && !Number.isInteger(forcedIndex)) return;
      lastActive = index;

      if (current) current.textContent = String(index + 1);
      if (currentCaption) {
        const slideCaption = slides[index].querySelector('figcaption');
        const slideLink = slideCaption && slideCaption.querySelector('a');
        const label = slideCaption ? slideCaption.textContent.trim().replace(/\s*↗\s*$/, '') : '';
        let captionLabel = currentCaption.querySelector('[data-artist-reel-caption-label]');
        if (!captionLabel) {
          captionLabel = document.createElement('span');
          captionLabel.className = 'artist-reel-test__link-label fg-arrow-link__label';
          captionLabel.dataset.artistReelCaptionLabel = '';
          currentCaption.replaceChildren(captionLabel);
        }
        captionLabel.textContent = label;
        currentCaption.querySelector('.artist-reel-test__link-arrow')?.remove();
        if (slideLink) {
          currentCaption.href = slideLink.href;
          const arrow = document.createElement('span');
          arrow.className = 'artist-reel-test__link-arrow fg-arrow-link__arrow';
          arrow.setAttribute('aria-hidden', 'true');
          arrow.textContent = '↗';
          currentCaption.appendChild(arrow);
        } else {
          currentCaption.removeAttribute('href');
        }
      }

      allSlides.forEach((slide) => slide.querySelectorAll('video').forEach((video) => video.pause()));
      if (!document.hidden && !motionQuery.matches) {
        const matchingSlides = allSlides.filter((slide) => Number(slide.dataset.artistReelIndex) === index);
        const nearest = matchingSlides.reduce((best, slide) => {
          if (!best) return slide;
          const edge = carousel.getBoundingClientRect().left;
          return Math.abs(slide.getBoundingClientRect().left - edge) < Math.abs(best.getBoundingClientRect().left - edge) ? slide : best;
        }, null);
        nearest?.querySelector('video')?.play().catch(() => {});
      }
    };

    const removeClones = () => {
      track.querySelectorAll('[data-artist-reel-clone]').forEach((clone) => clone.remove());
      allSlides = slides;
    };

    const cloneBatch = () => {
      const fragment = document.createDocumentFragment();
      slides.forEach((slide, index) => {
        const clone = slide.cloneNode(true);
        clone.dataset.artistReelClone = 'true';
        clone.dataset.artistReelIndex = String(index);
        clone.setAttribute('aria-hidden', 'true');
        clone.querySelectorAll('a,button,video').forEach((element) => element.tabIndex = -1);
        fragment.appendChild(clone);
      });
      return fragment;
    };

    const buildLoop = () => {
      const preservedIndex = Math.max(0, lastActive);
      removeClones();
      slideStarts = [];
      let cursor = 0;
      slides.forEach((slide) => {
        slideStarts.push(cursor);
        cursor += slide.getBoundingClientRect().width;
      });
      baseWidth = cursor;

      if (slides.length < 2 || !baseWidth) {
        loopStart = 0;
        offset = slideStarts[preservedIndex] || 0;
        render();
        update(preservedIndex);
        return;
      }

      const repeats = Math.max(2, Math.ceil(carousel.clientWidth / baseWidth) + 1);
      const before = document.createDocumentFragment();
      const after = document.createDocumentFragment();
      for (let index = 0; index < repeats; index += 1) before.appendChild(cloneBatch());
      for (let index = 0; index < repeats + 1; index += 1) after.appendChild(cloneBatch());
      track.insertBefore(before, slides[0]);
      track.appendChild(after);
      allSlides = [...track.querySelectorAll('[data-artist-reel-slide]')];
      loopStart = repeats * baseWidth;
      offset = loopStart + (slideStarts[preservedIndex] || 0);
      render();
      update(preservedIndex);
    };

    const pause = (duration = interactionPauseDuration) => {
      resumeAt = Math.max(resumeAt, performance.now() + duration);
    };

    const startSnap = (target) => {
      pause();
      if (motionQuery.matches) {
        offset = target;
        snap = null;
        render();
        update();
        return;
      }
      snap = { from: offset, to: target, start: performance.now(), duration: 360 };
    };

    const snapToNearest = () => {
      if (!baseWidth || slides.length < 2) return;
      const local = modulo(offset - loopStart, baseWidth);
      const cycleStart = offset - local;
      let target = cycleStart + slideStarts[0];
      let distance = Math.abs(target - offset);
      [-baseWidth, 0, baseWidth].forEach((cycleShift) => {
        slideStarts.forEach((start) => {
          const candidate = cycleStart + cycleShift + start;
          const candidateDistance = Math.abs(candidate - offset);
          if (candidateDistance < distance) {
            target = candidate;
            distance = candidateDistance;
          }
        });
      });
      startSnap(target);
    };

    const animate = (time) => {
      const elapsed = Math.min(64, time - lastTime);
      lastTime = time;
      if (snap) {
        const progress = Math.min(1, (time - snap.start) / snap.duration);
        const eased = 1 - Math.pow(1 - progress, 3);
        offset = snap.from + ((snap.to - snap.from) * eased);
        render();
        update();
        if (progress >= 1) snap = null;
      } else if (!motionQuery.matches && slides.length > 1 && pointerId === null && touchId === null && !document.hidden && time >= resumeAt) {
        offset += elapsed * 0.03;
        render();
        update();
      }
      animationFrame = window.requestAnimationFrame(animate);
    };

    const goBy = (delta) => {
      if (slides.length < 2 || !baseWidth) return;
      pause();
      const index = activeIndex();
      const wrapped = modulo(index + delta, slides.length);
      const local = modulo(offset - loopStart, baseWidth);
      const cycleStart = offset - local;
      let left = cycleStart + slideStarts[wrapped];
      if (delta > 0 && left <= offset + 1) left += baseWidth;
      if (delta < 0 && left >= offset - 1) left -= baseWidth;
      update(wrapped);
      startSnap(left);
    };

    buttons.forEach((button) => {
      button.addEventListener('click', () => goBy(Number(button.dataset.artistReelStep)));
    });
    track.addEventListener('wheel', (event) => {
      const horizontalDelta = Math.abs(event.deltaX) >= Math.abs(event.deltaY) ? event.deltaX : (event.shiftKey ? event.deltaY : 0);
      if (!horizontalDelta) return;
      event.preventDefault();
      snap = null;
      pause();
      offset += horizontalDelta;
      render();
      update();
      clearTimeout(wheelTimer);
      wheelTimer = window.setTimeout(snapToNearest, 140);
    }, { passive: false });

    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'touch' && 'ontouchstart' in window) return;
      if (event.button !== undefined && event.button !== 0) return;
      pointerId = event.pointerId;
      pointerStartX = event.clientX;
      pointerStartY = event.clientY;
      pointerStartOffset = offset;
      pointerAxis = null;
      dragDistance = 0;
      snap = null;
      pause();
      track.setPointerCapture?.(pointerId);
      track.classList.add('is-pointer-down');
    });

    track.addEventListener('pointermove', (event) => {
      if (event.pointerId !== pointerId) return;
      const deltaX = event.clientX - pointerStartX;
      const deltaY = event.clientY - pointerStartY;
      if (!pointerAxis && Math.max(Math.abs(deltaX), Math.abs(deltaY)) >= 5) {
        pointerAxis = Math.abs(deltaX) >= Math.abs(deltaY) ? 'x' : 'y';
      }
      if (pointerAxis !== 'x') return;
      event.preventDefault();
      dragDistance = Math.max(dragDistance, Math.abs(deltaX));
      offset = pointerStartOffset - deltaX;
      render();
      update();
    }, { passive: false });

    const releasePointer = (event) => {
      if (event.pointerId !== pointerId) return;
      track.releasePointerCapture?.(pointerId);
      const shouldSnap = pointerAxis === 'x';
      pointerId = null;
      pointerAxis = null;
      track.classList.remove('is-pointer-down');
      pause();
      if (shouldSnap) snapToNearest();
    };
    track.addEventListener('pointerup', releasePointer);
    track.addEventListener('pointercancel', releasePointer);
    track.addEventListener('lostpointercapture', (event) => {
      if (event.pointerId !== pointerId) return;
      pointerId = null;
      pointerAxis = null;
      track.classList.remove('is-pointer-down');
      pause();
      snapToNearest();
    });

    const touchById = (touches) => {
      for (let index = 0; index < touches.length; index += 1) {
        if (touches[index].identifier === touchId) return touches[index];
      }
      return null;
    };

    track.addEventListener('touchstart', (event) => {
      if (touchId !== null || event.touches.length !== 1) return;
      const touch = event.changedTouches[0];
      touchId = touch.identifier;
      touchStartX = touch.clientX;
      touchStartY = touch.clientY;
      touchStartOffset = offset;
      touchAxis = null;
      dragDistance = 0;
      snap = null;
      pause();
      track.classList.add('is-pointer-down');
    }, { passive: true });

    track.addEventListener('touchmove', (event) => {
      if (touchId === null) return;
      const touch = touchById(event.touches);
      if (!touch) return;
      const deltaX = touch.clientX - touchStartX;
      const deltaY = touch.clientY - touchStartY;
      if (!touchAxis && Math.max(Math.abs(deltaX), Math.abs(deltaY)) >= 5) {
        touchAxis = Math.abs(deltaX) >= Math.abs(deltaY) ? 'x' : 'y';
      }
      if (touchAxis !== 'x') return;
      event.preventDefault();
      dragDistance = Math.max(dragDistance, Math.abs(deltaX));
      offset = touchStartOffset - deltaX;
      render();
      update();
    }, { passive: false });

    const releaseTouch = (event) => {
      if (touchId === null || !touchById(event.changedTouches)) return;
      const shouldSnap = touchAxis === 'x';
      touchId = null;
      touchAxis = null;
      track.classList.remove('is-pointer-down');
      pause();
      if (shouldSnap) snapToNearest();
    };
    track.addEventListener('touchend', releaseTouch, { passive: true });
    track.addEventListener('touchcancel', releaseTouch, { passive: true });
    track.addEventListener('dragstart', (event) => event.preventDefault());

    track.addEventListener('click', (event) => {
      if (dragDistance <= 6) return;
      event.preventDefault();
      event.stopPropagation();
      dragDistance = 0;
    }, true);

    track.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      goBy(event.key === 'ArrowRight' ? 1 : -1);
    });
    document.addEventListener('visibilitychange', () => {
      lastTime = performance.now();
      update(activeIndex());
    });
    motionQuery.addEventListener?.('change', buildLoop);
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(buildLoop, 160);
    });

    buildLoop();
    animationFrame = window.requestAnimationFrame(animate);
    reel.addEventListener('artist-reel-destroy', () => {
      window.cancelAnimationFrame(animationFrame);
      clearTimeout(wheelTimer);
      clearTimeout(resizeTimer);
    }, { once: true });
  };

  const initInfo = (toggle) => {
    if (toggle.dataset.artistInfoReady === 'true') return;
    const panel = document.getElementById(toggle.getAttribute('aria-controls'));
    const symbol = toggle.querySelector('[data-artist-info-symbol]');
    const identityHeader = toggle.closest('.artist-reel-test__header');
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!panel) return;
    toggle.dataset.artistInfoReady = 'true';

    const finishOpen = () => {
      panel.style.height = 'auto';
      panel.removeEventListener('transitionend', finishOpen);
    };
    const finishClose = () => {
      panel.hidden = true;
      panel.style.height = '';
      panel.removeEventListener('transitionend', finishClose);
    };

    toggle.addEventListener('click', () => {
      const open = toggle.getAttribute('aria-expanded') !== 'true';
      toggle.setAttribute('aria-expanded', String(open));
      if (symbol) symbol.textContent = open ? '−' : '+';

      if (reduceMotion) {
        panel.hidden = !open;
        panel.classList.toggle('is-open', open);
        panel.style.height = open ? 'auto' : '';
        if (open) identityHeader?.scrollIntoView({ block: 'start' });
        return;
      }

      if (open) {
        panel.hidden = false;
        panel.style.height = '0px';
        panel.classList.add('is-open');
        window.requestAnimationFrame(() => {
          panel.style.height = `${panel.scrollHeight}px`;
          panel.addEventListener('transitionend', finishOpen);
          identityHeader?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      } else {
        panel.style.height = `${panel.scrollHeight}px`;
        window.requestAnimationFrame(() => {
          panel.classList.remove('is-open');
          panel.style.height = '0px';
          panel.addEventListener('transitionend', finishClose);
        });
      }
    });
  };

  const init = (root = document) => {
    root.querySelectorAll('[data-artist-reel]').forEach(initReel);
    root.querySelectorAll('[data-artist-info-toggle]').forEach(initInfo);
  };
  init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
